"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const lodash_1 = __importDefault(require("lodash"));
const react_1 = __importDefault(require("react"));
const react_2 = require("react");
const GridComponent_1 = require("@mwater/react-library/lib/GridComponent");
const expressions_1 = require("@mwater/expressions");
const ExprCellComponent_1 = __importDefault(require("./ExprCellComponent"));
const immer_1 = __importDefault(require("immer"));
const CellEditor_1 = require("./CellEditor");
const useStableCallback_1 = require("@mwater/react-library/lib/useStableCallback");
const valueFormatter_1 = require("../valueFormatter");
const valueFormatter_2 = require("../valueFormatter");
/** Datagrid view component. It does not show any quick filters, but only the grid itself. */
const DatagridViewComponent = (0, react_2.forwardRef)((props, ref) => {
    const [rows, setRows] = (0, react_2.useState)([]);
    const [entirelyLoaded, setEntirelyLoaded] = (0, react_2.useState)(false);
    const loadStateRef = (0, react_2.useRef)(null);
    // Get row header width based on selection and showRowNumbers
    const rowHeaderWidth = props.selectedRows
        ? (props.design.showRowNumbers ? 70 : 30) // With selection: 70 for both, 30 for just checkbox
        : (props.design.showRowNumbers ? 50 : 20); // Without selection: 50 for numbers, 20 for neither
    const handleRowSelect = (0, react_2.useCallback)((rowId) => {
        if (props.onSelectedRowsChange) {
            props.onSelectedRowsChange(props.selectedRows.toggle(rowId));
        }
    }, [props.selectedRows, props.onSelectedRowsChange]);
    const handleSelectAll = (0, react_2.useCallback)(() => {
        if (props.onSelectedRowsChange) {
            props.onSelectedRowsChange(props.selectedRows.all());
        }
    }, [props.selectedRows, props.onSelectedRowsChange]);
    const handleSelectNone = (0, react_2.useCallback)(() => {
        if (props.onSelectedRowsChange) {
            props.onSelectedRowsChange(props.selectedRows.none());
        }
    }, [props.selectedRows, props.onSelectedRowsChange]);
    const handleSelectInvert = (0, react_2.useCallback)(() => {
        if (props.onSelectedRowsChange) {
            props.onSelectedRowsChange(props.selectedRows.invert());
        }
    }, [props.selectedRows, props.onSelectedRowsChange]);
    /** Render top left corner with optional selection dropdown */
    const renderTopLeft = (0, react_2.useCallback)(() => {
        if (!props.selectedRows) {
            return null;
        }
        if (!props.onSelectedRowsChange) {
            return (react_1.default.createElement("div", { style: { padding: 8, textAlign: "right" } },
                react_1.default.createElement("input", { type: "checkbox", checked: false, readOnly: true })));
        }
        return (react_1.default.createElement("div", { className: "dropdown", style: { marginTop: 6 } },
            react_1.default.createElement("div", { style: { textAlign: "center" }, className: "dropdown-toggle", "data-bs-toggle": "dropdown" },
                react_1.default.createElement("input", { type: "checkbox", checked: false, readOnly: true, style: { marginTop: 7 } })),
            react_1.default.createElement("ul", { className: "dropdown-menu" },
                react_1.default.createElement("li", null,
                    react_1.default.createElement("a", { className: "dropdown-item", onClick: handleSelectAll }, T `All`)),
                react_1.default.createElement("li", null,
                    react_1.default.createElement("a", { className: "dropdown-item", onClick: handleSelectNone }, T `None`)),
                react_1.default.createElement("li", null,
                    react_1.default.createElement("a", { className: "dropdown-item", onClick: handleSelectInvert }, T `Invert`)))));
    }, [props.selectedRows, props.onSelectedRowsChange, handleSelectAll, handleSelectNone, handleSelectInvert]);
    /** Render row headers with optional selection checkboxes */
    const renderRowHeader = (0, react_2.useCallback)((options) => {
        const rowHeaderStyle = {
            width: options.width,
            height: options.height,
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            padding: "0 8px"
        };
        const rowId = options.row < rows.length ? rows[options.row].id : null;
        const isSelected = rowId != null && props.selectedRows ? props.selectedRows.isSelected(rowId) : false;
        const isSubtable = options.row < rows.length ? rows[options.row].subtable >= 0 : false;
        return (react_1.default.createElement("div", { style: rowHeaderStyle },
            props.selectedRows && !isSubtable && (react_1.default.createElement("input", { type: "checkbox", style: { marginRight: 4 }, checked: isSelected, onChange: rowId != null && props.onSelectedRowsChange ? () => handleRowSelect(rowId) : undefined, readOnly: !props.onSelectedRowsChange })),
            props.design.showRowNumbers ? (react_1.default.createElement("div", { style: { flex: 1, textAlign: "right" } }, options.row + 1)) : null));
    }, [rows, props.selectedRows, props.onSelectedRowsChange, props.design.showRowNumbers, handleRowSelect]);
    const loadMoreRows = (0, useStableCallback_1.useStableCallback)((callback) => {
        const loadState = {
            design: props.design,
            offset: rows.length,
            limit: props.pageSize || 100,
            filters: props.filters
        };
        if (lodash_1.default.isEqual(loadState, loadStateRef.current)) {
            return;
        }
        loadStateRef.current = loadState;
        props.datagridDataSource.getRows(loadState.design, loadState.offset, loadState.limit, loadState.filters, (error, newRows) => {
            if (error) {
                console.error(error);
                alert(T `Error loading data`);
                return;
            }
            if (lodash_1.default.isEqual(loadState, loadStateRef.current)) {
                loadStateRef.current = null;
                setRows(prev => [...prev, ...newRows]);
                setEntirelyLoaded(newRows.length < (props.pageSize || 100));
                callback?.();
            }
        });
    });
    function reload() {
        console.log("reload");
        setRows([]);
        setEntirelyLoaded(false);
    }
    // Reset rows when design, filters or refreshKey changes
    (0, react_2.useEffect)(() => {
        reload();
    }, [getDatagridReloadKey(props.design), JSON.stringify(props.filters), props.refreshKey]);
    function getRows() {
        return rows;
    }
    function reloadRow(rowIndex, rowId, callback) {
        const filters = (0, immer_1.default)(props.filters || [], draft => {
            draft.push({
                table: props.design.table,
                jsonql: {
                    type: "op",
                    op: "=",
                    exprs: [
                        { type: "field", tableAlias: "{alias}", column: props.schema.getTable(props.design.table)?.primaryKey },
                        { type: "literal", value: rowId }
                    ]
                }
            });
        });
        props.datagridDataSource.getRows(props.design, 0, 1, filters, (error, newRows) => {
            if (error) {
                console.error(error);
                alert(T `Error loading data`);
                callback();
                return;
            }
            setRows(currentRows => (0, immer_1.default)(currentRows, draft => {
                if (newRows[0]) {
                    draft[rowIndex] = newRows[0];
                }
                else {
                    draft.splice(rowIndex, 1);
                }
            }));
            callback();
        });
    }
    function deleteRow(rowIndex, callback) {
        const newRows = rows.slice();
        newRows.splice(rowIndex, 1);
        setRows(newRows);
        callback();
    }
    // Expose methods via ref
    (0, react_2.useImperativeHandle)(ref, () => ({
        getRows: getRows,
        getEntirelyLoaded: () => entirelyLoaded,
        reload: reload,
        reloadRow: reloadRow,
        deleteRow: deleteRow,
        loadMoreRows: loadMoreRows
    }), [rows, entirelyLoaded, reload, reloadRow, deleteRow, loadMoreRows]);
    const handleColWidthsChange = (0, useStableCallback_1.useStableCallback)((colWidths) => {
        const columns = props.design.columns.map((col, index) => ({
            ...col,
            width: colWidths[index]
        }));
        props.onDesignChange?.({ ...props.design, columns });
    });
    const updateCellValue = (0, useStableCallback_1.useStableCallback)(async (value, rowIndex, colIndex) => {
        try {
            const rowId = rows[rowIndex].id;
            const { expr } = props.design.columns[colIndex];
            await props.updateExprValues(props.design.table, [{ primaryKey: rowId, expr, value }]);
            await new Promise(resolve => {
                reloadRow(rowIndex, rowId, resolve);
            });
            return true;
        }
        catch (error) {
            alert(T `Error saving data`);
            console.error(error);
            return false;
        }
    });
    const handleRowDoubleClick = (0, useStableCallback_1.useStableCallback)((rowIndex) => {
        if (props.onRowDoubleClick != null && rows[rowIndex]?.id) {
            props.onRowDoubleClick(props.design.table, rows[rowIndex].id, rowIndex);
            return true;
        }
        return false;
    });
    const handleRowClick = (0, useStableCallback_1.useStableCallback)((rowIndex) => {
        if (props.onRowClick != null && rows[rowIndex]?.id) {
            props.onRowClick(props.design.table, rows[rowIndex].id, rowIndex);
            return true;
        }
        return false;
    });
    const handleCopy = (0, useStableCallback_1.useStableCallback)((rowIndex, colIndex) => {
        const row = rows[rowIndex];
        const col = props.design.columns[colIndex];
        const value = row[`c${colIndex}`];
        // Format value as text using ExprCellComponent logic
        const exprUtils = new expressions_1.ExprUtils(props.schema);
        const exprType = exprUtils.getExprType(col.expr);
        if (!exprType) {
            return;
        }
        let formattedValue = value;
        // Parse JSON values
        if (["image", "imagelist", "geometry", "text[]"].includes(exprType) && lodash_1.default.isString(value)) {
            formattedValue = JSON.parse(value);
        }
        // Format based on type
        if ((0, valueFormatter_2.canFormatType)(exprType)) {
            formattedValue = (0, valueFormatter_1.formatValue)(exprType, formattedValue, col.format);
        }
        else {
            formattedValue = exprUtils.stringifyExprLiteral(col.expr, formattedValue, props.design.locale);
        }
        navigator.clipboard.writeText(formattedValue || "");
    });
    const renderCell = (0, useStableCallback_1.useStableCallback)((options) => {
        if (options.row >= rows.length) {
            lodash_1.default.defer(() => loadMoreRows());
            return react_1.default.createElement("div", { style: { padding: 8 } },
                react_1.default.createElement("i", { className: "fa fa-spinner fa-spin text-muted" }));
        }
        if (options.col === -1) {
            return (react_1.default.createElement("div", { style: { padding: 8, textAlign: "right" } }, options.row + 1));
        }
        const column = props.design.columns[options.col];
        const exprUtils = new expressions_1.ExprUtils(props.schema);
        const exprType = exprUtils.getExprType(column.expr);
        const value = rows[options.row][`c${options.col}`];
        if (column.type === "expr" && exprType) {
            const muted = !column.subtable && rows[options.row].subtable >= 0;
            return react_1.default.createElement("div", { style: { width: options.width, height: options.height } },
                react_1.default.createElement(ExprCellComponent_1.default, { schema: props.schema, dataSource: props.dataSource, locale: props.design.locale, width: options.width, height: options.height, value: value, expr: column.expr, format: column.format, exprType: exprType, muted: muted }));
        }
        return null;
    });
    const renderColHeader = (0, useStableCallback_1.useStableCallback)((options) => {
        if (options.col === -1) {
            return (react_1.default.createElement("div", { style: {
                    padding: 5,
                    textAlign: "right",
                    fontWeight: "bold",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end"
                } }, "#"));
        }
        const column = props.design.columns[options.col];
        const exprUtils = new expressions_1.ExprUtils(props.schema);
        return (react_1.default.createElement("div", { style: {
                padding: 5,
                whiteSpace: "nowrap",
                fontWeight: "bold",
                height: "100%",
                display: "flex",
                alignItems: "center"
            } }, column.label || exprUtils.summarizeExpr(column.expr, props.design.locale)));
    });
    const renderCellEditor = (0, useStableCallback_1.useStableCallback)((options) => {
        if (options.row >= rows.length) {
            return react_1.default.createElement("div", null);
        }
        const column = props.design.columns[options.col];
        const value = rows[options.row][`c${options.col}`];
        const expr = column.expr;
        const exprUtils = new expressions_1.ExprUtils(props.schema);
        const exprType = exprUtils.getExprType(expr);
        const enumValues = exprUtils.getExprEnumValues(expr) ?? undefined;
        const idTable = exprUtils.getExprIdTable(expr) ?? undefined;
        return (react_1.default.createElement(CellEditor_1.CellEditor, { type: exprType, enumValues: enumValues, idTable: idTable, schema: props.schema, dataSource: props.dataSource, initialValue: value, updateCellValue: value => updateCellValue(value, options.row, options.col), editorOptions: options }));
    });
    const canEdit = (0, useStableCallback_1.useStableCallback)(async (options) => {
        if (options.col === -1) {
            return false;
        }
        const rowId = rows[options.row].id;
        if (rowId == null) {
            return false;
        }
        if (!props.canEditExpr) {
            return false;
        }
        const column = props.design.columns[options.col];
        const exprType = new expressions_1.ExprUtils(props.schema).getExprType(column.expr);
        if (!(0, CellEditor_1.canEditType)(exprType)) {
            return false;
        }
        return props.canEditExpr(props.design.table, rowId, column.expr);
    });
    let numRows = rows.length;
    if (!entirelyLoaded) {
        numRows += 1;
    }
    const colWidths = props.design.columns.map(col => col.width || 200);
    return (react_1.default.createElement(GridComponent_1.GridComponent, { width: props.width, height: props.height, numRows: numRows, colWidths: colWidths, colHeaderHeight: 40, rowHeight: 40, renderCell: renderCell, renderColHeader: renderColHeader, renderCellEditor: renderCellEditor, canEdit: canEdit, onColWidthsChange: handleColWidthsChange, onRowDoubleClick: handleRowDoubleClick, onRowClick: handleRowClick, rowHeaderWidth: rowHeaderWidth, renderRowHeader: renderRowHeader, renderTopLeft: renderTopLeft, onCopy: handleCopy }));
});
exports.default = DatagridViewComponent;
/** Get a unique key for a datagrid design, ignoring column widths which don't affect data */
function getDatagridReloadKey(design) {
    // Create copy without column widths
    const designWithoutWidths = (0, immer_1.default)(design, draft => {
        for (const column of draft.columns) {
            column.width = 0;
        }
    });
    return JSON.stringify(designWithoutWidths);
}
